import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MailCheck, Clock, RotateCw, Copy, Loader2, AlertCircle, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const FOLLOW_UP_DAYS = 3;
const daysSince = (iso) => (Date.now() - new Date(iso).getTime()) / 86400000;
const url = (t) => `${window.location.origin}/request/${t}`;

const Action = ({ i, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  if (i.submitted_at) return null;
  if (i.channel === "link" || !i.email) return (
    <span className="inline-flex gap-1">
      <Button size="sm" variant="ghost" data-testid={`form-invite-copy-${i.id}`} onClick={() => navigator.clipboard.writeText(url(i.token)).then(() => toast.success("Link copied")).catch(() => toast.error(url(i.token)))}><Copy className="h-4 w-4 mr-1" />Copy link</Button>
      <Button asChild size="sm" variant="ghost"><a data-testid={`form-invite-text-${i.id}`} href={`sms:?&body=${encodeURIComponent(`Hi ${i.name.split(" ")[0]}, here's the short form to request your feasibility study: ${url(i.token)}`)}`}><MessageSquare className="h-4 w-4 mr-1" />Text it</a></Button>
    </span>
  );
  const resend = async () => {
    setBusy(true);
    try { const r = await api.post(`/form-invites/${i.id}/remind`); onUpdated(r.data); toast.success(`Reminder sent to ${i.email}`); }
    catch (e) { toast.error(errMsg(e, "Could not send the reminder")); } finally { setBusy(false); }
  };
  return <Button size="sm" variant="outline" data-testid={`form-invite-resend-${i.id}`} disabled={busy} onClick={resend} className="rounded-full border-slate-700 bg-transparent">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCw className="h-4 w-4 mr-1" />}Resend</Button>;
};

const Status = ({ i }) => {
  if (i.submitted_at) return <span className="inline-flex items-center gap-1 text-emerald-300"><MailCheck className="h-4 w-4" />Submitted {new Date(i.submitted_at).toLocaleDateString()}</span>;
  const follow = daysSince(i.last_reminded_at || i.created_at) >= FOLLOW_UP_DAYS;
  return (
    <span className="space-y-0.5">
      {follow ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-300" data-testid={`form-invite-followup-${i.id}`}><AlertCircle className="h-3.5 w-3.5" />Follow up</span>
        : <span className="inline-flex items-center gap-1 text-slate-400"><Clock className="h-4 w-4" />Waiting</span>}
      {i.reminders > 0 && <span className="block text-[11px] text-slate-500" data-testid={`form-invite-reminders-${i.id}`}>{i.reminders} reminder{i.reminders > 1 ? "s" : ""}{i.auto_reminders ? ` (${i.auto_reminders} automatic)` : ""}, last {new Date(i.last_reminded_at).toLocaleDateString()}</span>}
    </span>
  );
};

export const FormInvitesLog = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => api.get("/form-invites").then((r) => setItems(r.data)).catch(() => {});
    load();
    window.addEventListener("form-invites:changed", load);
    return () => window.removeEventListener("form-invites:changed", load);
  }, []);
  if (!items.length) return null;
  const update = (d) => setItems((l) => l.map((x) => (x.id === d.id ? d : x)));
  return (
    <section className="mt-10 rounded-xl border border-slate-800 bg-slate-900/70 overflow-x-auto" data-testid="form-invites-log">
      <h2 className="font-display text-lg font-semibold px-5 pt-5 pb-3">Sent request forms</h2>
      <table className="w-full text-sm min-w-[720px] m-cards">
        <thead className="text-xs uppercase tracking-wider text-slate-500"><tr>{["Prospect", "Channel", "Invited by", "Sent", "Status", ""].map((h) => <th key={h} className="text-left px-5 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className={`border-t border-slate-800 ${!i.submitted_at && daysSince(i.last_reminded_at || i.created_at) >= FOLLOW_UP_DAYS ? "bg-amber-500/[0.04]" : ""}`} data-testid={`form-invite-${i.id}`}>
              <td className="px-5 py-3"><p className="font-semibold">{i.name}</p><p className="text-xs text-slate-400">{i.email || "No email"}</p></td>
              <td data-label="Channel" className="px-5 py-3" data-testid={`form-invite-channel-${i.id}`}><span className={`text-xs px-2 py-0.5 rounded-full ${i.channel === "link" ? "bg-sky-500/15 text-sky-300" : "bg-slate-800 text-slate-300"}`}>{i.channel === "link" ? "Link" : "Email"}</span></td>
              <td data-label="Invited by" className="px-5 py-3 text-slate-300">{i.invited_by}</td>
              <td data-label="Sent" className="px-5 py-3 text-slate-400">{new Date(i.created_at).toLocaleDateString()}</td>
              <td data-label="Status" className="px-5 py-3" data-testid={`form-invite-status-${i.id}`}><Status i={i} /></td>
              <td className="px-5 py-3 text-right"><Action i={i} onUpdated={update} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};
